import {
  ComputerDesktopFrame,
  OpenComputerButton,
  revokeComputerDesktopLink,
  SharedThreadNotice,
} from "@/components/artifacts/view/blocks/ComputerDesktopBlock.tsx";
import { useIsSharedThread } from "@/hooks/useIsSharedThread.ts";
import { messageQueries } from "@/shared/messageQueries.ts";
import { useQueryClient } from "@tanstack/react-query";
import { atom, useAtom, useSetAtom, useStore } from "jotai";
import { useEffect } from "react";

/**
 * The desktop opened from the card above the composer, the thread it was
 * opened in, the route that revokes its link, and the route that pastes into
 * it.
 *
 * Held here rather than in the card because opening the computer is what moves
 * the card out of that space: the next refetch of the thread — the one this
 * body asks for, a tab coming back into focus, a socket push — unmounts it. The
 * overlay lives in `ComputerDesktopOverlay`, which the composer footer keeps
 * mounted, so the desktop stays open while the card goes.
 */
const openComputerDesktopAtom = atom<{
  threadId: number;
  url: string;
  closePath: string | undefined;
  pastePath: string | undefined;
} | null>(null);

/**
 * The computer card above the composer: the "Open computer" button itself, in
 * place of a summary the user would have to click through to reach it.
 *
 * The server moves the card on once the desktop link is minted, so the thread
 * is refreshed straight away and the card drops back to its chip in the thread.
 */
export const ComputerDesktopOmniBody = ({
  openPath,
  closePath,
  pastePath,
  threadId,
}: {
  openPath: string;
  closePath?: string;
  pastePath?: string;
  threadId: number;
}) => {
  const queryClient = useQueryClient();
  const isSharedThread = useIsSharedThread();
  const setOpenDesktop = useSetAtom(openComputerDesktopAtom);

  if (isSharedThread) return <SharedThreadNotice />;

  return (
    <OpenComputerButton
      openPath={openPath}
      onOpened={(url) => {
        setOpenDesktop({ threadId, url, closePath, pastePath });
        void queryClient.invalidateQueries({ queryKey: messageQueries.thread(threadId) });
      }}
    />
  );
};

/**
 * The desktop opened from the card above the composer, while it is open.
 *
 * Leaving the thread drops it. Coming back would otherwise reopen the desktop
 * unasked, on a link that may have expired in the meantime. Dropping it also
 * asks the server to revoke the link.
 */
export const ComputerDesktopOverlay = ({ threadId }: { threadId: number }) => {
  const [openDesktop, setOpenDesktop] = useAtom(openComputerDesktopAtom);
  const store = useStore();
  // The desktop this overlay is showing: the open one, if it is this thread's.
  const shown = openDesktop?.threadId === threadId ? openDesktop : null;

  // Revoked and cleared from an effect's cleanup, so every way the desktop
  // stops being shown ends its link: closing the overlay, the overlay
  // unmounting, a second link replacing the first, and switching straight to
  // another thread. That last one does not unmount this component — it stays
  // mounted and is handed a new `threadId` — so a cleanup tied to unmount alone
  // would leave the link live in the atom, to reopen on the way back.
  useEffect(() => {
    if (shown === null) return;
    return () => {
      revokeComputerDesktopLink(shown.closePath, shown.url);
      // Only if it is still the one this effect was showing. A newer link has
      // its own effect, and must not be cleared by the older one's cleanup.
      store.set(openComputerDesktopAtom, (current) => (current === shown ? null : current));
    };
  }, [shown, store]);

  return (
    <ComputerDesktopFrame
      url={shown?.url ?? null}
      pastePath={shown?.pastePath}
      onClose={() => setOpenDesktop(null)}
    />
  );
};
